// Rewards Progress Web Component
class RewardsProgress extends HTMLElement {
  constructor() {
    super();
    this.rewardsData = null;
    this.currentValue = 0;
    this.step = 10;
    this.minValue = 0;
    this.maxValue = 0;
    this.milestones = [];
    this.processedCards = new Set(); // Track which cards have already been processed
    this.lastBreakpoint = null; // Track current breakpoint for resize handling
    this.isMobile = window.innerWidth < 600;
  }

  static get observedAttributes() {
    return ['data-current-value', 'data-step'];
  }

  connectedCallback() {
    this.parseData();
    this.setupComponent();
    this.setupEventListeners();
    this.initializeCanvasConnector();
    
    // Check for reached tiers with products
    this.checkReachedTiersWithProducts();
    
    // Set up resize listener for mobile/desktop switching
    this.boundHandleResize = this.handleResize.bind(this);
    window.addEventListener('resize', this.boundHandleResize);
    
    // Set initial breakpoint
    this.lastBreakpoint = window.innerWidth < 600 ? 'mobile' : 'desktop';
  }

  disconnectedCallback() {
    // Clean up event listeners
    if (this.boundHandleCartUpdate) {
      document.removeEventListener('cart:updated', this.boundHandleCartUpdate);
    }
    
    if (this.boundHandleResize) {
      window.removeEventListener('resize', this.boundHandleResize);
    }
    
    // Clean up canvas connector
    if (this.canvasConnector) {
      this.canvasConnector.destroy();
      this.canvasConnector = null;
    }
    
    // Clean up mobile swiper
    this.destroyMobileSwiper();
  }

  parseData() {
    // Parse data from script tag inside the component (BEFORE clearing innerHTML)
    const scriptTag = this.querySelector('script[type="application/json"]');
    if (!scriptTag) {
      console.warn('RewardsProgress: No data script tag found');
      return;
    }

    try {
      this.rewardsData = JSON.parse(scriptTag.textContent);
      console.log('Parsed data', this.rewardsData)
      this.quantityAdjustment(this.rewardsData);
    } catch (error) {
      console.warn('RewardsProgress: Invalid JSON data', error);
      return;
    }
  }

  async quantityAdjustment(rewardsData) {
    try {
      if (!rewardsData || !rewardsData.cart || !Array.isArray(rewardsData.cart.items)) {
        return;
      }

      const tiers = Array.isArray(rewardsData.tiers) ? rewardsData.tiers : [];
      const giftProductIds = new Set();

      tiers.forEach((tier) => {
        if (tier && tier.product && tier.product.id) {
          giftProductIds.add(Number(tier.product.id));
        }
      });

      if (!giftProductIds.size) {
        return;
      }

      const adjustments = rewardsData.cart.items.filter((item) => {
        if (!item || typeof item.quantity !== 'number') return false;
        if (item.quantity <= 1) return false;
        const productId = Number(item.product_id);
        if (!giftProductIds.has(productId)) return false;
        const isGiftItem = item.properties && item.properties.isGiftItem === true;
        const isZeroPrice = Number(item.price) === 0;
        return isGiftItem || isZeroPrice;
      });

      for (const item of adjustments) {
        try {
          await this.changeLineQuantityByKey(item.key, 1);
        } catch (error) {
          console.warn(`[RewardsProgress] Failed to adjust quantity for gift product ${item.product_id}`, error);
        }
      }
    } catch (error) {
      console.warn('RewardsProgress: quantityAdjustment failed', error);
    }
  }
  setupComponent() {
    // Check if rewardsData is available
    if (!this.rewardsData || !this.rewardsData.tiers) {
      console.warn('RewardsProgress: rewardsData or tiers not available');
      return;
    }

    // Set up component properties
    // Priority: 1) Cart total from data, 2) data-current-value attribute, 3) default to 0
    let initialValue = 0;
    
    // Check if cart data is available in rewardsData
    if (this.rewardsData.cart && this.rewardsData.cart.total_price !== undefined) {
      // Convert from cents to dollars
      initialValue = this.rewardsData.cart.total_price / 100;
    } else {
      // Fall back to data attribute
      initialValue = parseInt(this.getAttribute('data-current-value')) || 0;
    }
    
    this.currentValue = initialValue;
    this.step = parseInt(this.getAttribute('data-step')) || this.rewardsData.step || 10;
    this.maxValue = Math.max(...this.rewardsData.tiers.map(tier => tier.threshold)) + 50;
    this.milestones = this.rewardsData.tiers.map(tier => tier.threshold);
    
    // Set data attribute for external access
    this.setAttribute('data-current-value', this.currentValue);

    // Find and clone the template
    const template = this.querySelector('template#rewards-template');
    if (template) {
      // Clear existing content except script and template tags
      const scriptTag = this.querySelector('script[type="application/json"]');
      this.innerHTML = '';
      
      // Clone and append the template content
      const clone = template.content.cloneNode(true);
      this.appendChild(clone);
      
      // Re-append the script tag (for reference if needed)
      if (scriptTag) {
        this.appendChild(scriptTag);
      }
    } else {
      // Fallback to inline HTML if no template is found
      this.createDefaultTemplate();
    }

    // Initialize the component
    this.generateMilestones();
    this.generateRewardCards();
    this.updateProgress();

    // Resolve animation delay from attribute or default
    const delayAttr = parseInt(this.getAttribute('data-animation-delay'));
    this.animationDelayMs = Number.isFinite(delayAttr) ? delayAttr : 1500;

    // Add 'loaded' class to rewards-cover after delay
    // This will enable all animations inside rewards-container
    const rewardsCover = this.querySelector('.rewards-cover');
    if (rewardsCover) {
      setTimeout(() => {
        rewardsCover.classList.add('loaded');
        // Add class to parent element to enable animations
        this.classList.add('animations-enabled');
        
        // If cards already exist with animate-in class, manually trigger connector creation
        // because their animations might not have fired (were created before animations enabled)
        const existingCards = this.querySelectorAll('.reward-card.animate-in');
        if (existingCards.length > 0) {
          // Get the cards data to pass to connector creation
          const rewardsCardsContainer = this.querySelector('#rewardsCards');
          if (rewardsCardsContainer) {
            const allCards = Array.from(rewardsCardsContainer.querySelectorAll('.reward-card'));
            const cardsToShow = allCards.map(card => ({
              threshold: parseInt(card.getAttribute('data-threshold')) || 0
            }));
            
            // Wait a bit for animations to potentially start, then trigger connector
            setTimeout(() => {
              this.triggerConnectorCreation(cardsToShow);
            }, 550); // Slightly longer than card animation duration
          }
        }
      }, this.animationDelayMs);
    }
  }

  createDefaultTemplate() {
    // Fallback method if no template is provided
    this.innerHTML = `
      <div class="rewards-container">
        <div class="progress-section">
          <div class="progress-bar" id="progressBar">
            <div class="progress-fill"></div>
            <!-- Milestones will be dynamically generated here -->
          </div>
        </div>

        <div class="rewards-cards" id="rewardsCards">
          <!-- Dynamic reward cards will be generated here -->
        </div>
      </div>
    `;
  }

  setupEventListeners() {
    // Listen for cart:updated events
    this.boundHandleCartUpdate = this.handleCartUpdate.bind(this);
    document.addEventListener('cart:updated', this.boundHandleCartUpdate);
  }

  initializeCanvasConnector() {
    // Check if canvas connector is enabled via data attribute
    const canvasConnectorEnabled = this.getAttribute('data-canvas-connector') === 'true';
    
    if (!canvasConnectorEnabled) {
      return;
    }
    
    // Clean up any existing connector first
    if (this.canvasConnector) {
      this.canvasConnector.destroy();
      this.canvasConnector = null;
    }
    
    const container = this.querySelector('.rewards-container');
    if (container) {
      // Initialize canvas connector with a slight delay to ensure DOM is fully rendered
      setTimeout(() => {
        const connector = new CanvasConnector();
        const success = connector.init(container);
        
        if (success) {
          // Store connector instance on component for access
          this.canvasConnector = connector;
          
          // Don't draw connectors immediately - wait for card animations to complete
          // Connectors will be generated by rewards:animationsComplete event
        } else {
          console.warn('RewardsProgress: Failed to initialize canvas connector');
        }
      }, 100);
    }
  }

  handleCartUpdate(event) {
    // Extract cart total from event detail
    let cartTotal = 0;
    
    try {
      // Check if cart data is in the event detail
      if (event.detail && event.detail.cart) {
        // Parse the cart JSON string
        const cartData = typeof event.detail.cart === 'string' ? JSON.parse(event.detail.cart) : event.detail.cart;
        // Get total_price (in cents) and convert to dollars
        if (cartData.total_price !== undefined) {
          cartTotal = cartData.total_price / 100;
        }
      } else {
        // Fallback to other possible formats
        cartTotal = event.detail?.total || event.detail?.value || event.detail || 0;
      }
    } catch (error) {
      console.warn('RewardsProgress: Error parsing cart data', error);
      // Fallback
      cartTotal = event.detail?.total || event.detail?.value || event.detail || 0;
    }
    
    // Update the component value
    this.setValue(cartTotal);
  }

  generateMilestones() {
    // Check if rewardsData is available
    if (!this.rewardsData || !this.rewardsData.tiers) {
      console.warn('generateMilestones: rewardsData not available');
      return;
    }

    const progressBar = this.querySelector('#progressBar');
    if (!progressBar) {
      return;
    }

    // Sort tiers by threshold value (lowest to highest) to ensure correct order
    const sortedTiers = [...this.rewardsData.tiers].sort((a, b) => a.threshold - b.threshold);
    const numberOfMilestones = sortedTiers.length;

    sortedTiers.forEach((tier, index) => {
      const milestoneDiv = document.createElement('div');
      milestoneDiv.className = `milestone ${tier.locked ? 'locked' : ''}`;
      
      // Add threshold data attribute for connector system
      milestoneDiv.setAttribute('data-threshold', tier.threshold);

      // Calculate position based on number of milestones
      let position;
      if (numberOfMilestones === 1) {
        position = 50; // Center if only one milestone
      } else if (numberOfMilestones === 2) {
        // For 2 milestones: use equal spacing (3 sections)
        const sectionWidth = 100 / 3; // 33.33% per section
        position = sectionWidth * (index + 1); // 33.33% and 66.67%
      } else {
        // For 3+ milestones: use 10%-90% range with equal spacing between
        const startPosition = 10;
        const endPosition = 90;
        const spacing = (endPosition - startPosition) / (numberOfMilestones - 1);
        position = startPosition + (spacing * index);
      }

      milestoneDiv.style.left = `${position}%`;
      milestoneDiv.style.transform = 'translateX(-50%)';

      // Create milestone icon
      const iconDiv = document.createElement('div');
      iconDiv.className = 'milestone-icon';
      
      // Create icon span and set background via CSS variable
      const iconSpan = document.createElement('span');
      
      // Check if icon is a URL (http/https or starts with /) or an emoji/text
      const isImageUrl = tier.icon && (tier.icon.startsWith('http') || tier.icon.startsWith('/') || tier.icon.includes('.'));
      
      // Only set text content for emoji/text icons, not for URLs
      if (tier.icon && !isImageUrl) {
        iconSpan.textContent = tier.icon || '🎁';
      }
      iconDiv.appendChild(iconSpan);

      // Set CSS custom property for background image
      if (isImageUrl) {
        iconDiv.style.setProperty('--background', `url(${tier.icon})`);
      }

      // Create milestone value (convert from cents to dollars)
      const valueDiv = document.createElement('div');
      valueDiv.className = 'milestone-value';
      valueDiv.textContent = tier.thresholdInMoney || `$${thresholdInDollars}`;

      // Create milestone label
      const labelDiv = document.createElement('div');
      labelDiv.className = 'milestone-label';
      labelDiv.innerHTML = tier.label;

      milestoneDiv.appendChild(iconDiv);
      milestoneDiv.appendChild(valueDiv);
      milestoneDiv.appendChild(labelDiv);

      progressBar.appendChild(milestoneDiv);
    });
  }

  generateRewardCards() {
    if (!this.rewardsData || !this.rewardsData.tiers) {
      console.warn('generateRewardCards: rewardsData not available');
      return;
    }

    const rewardsCardsContainer = this.querySelector('#rewardsCards');
    if (!rewardsCardsContainer) {
      return;
    }
    
    // Track previous cards for animation detection
    const previousCards = this.previousCardState || [];
    
    // Find cards to display (always show exactly 2 cards)
    // Show the next 2 tiers that user is progressing toward
    const cardsToShow = [];
    
    // Collect all milestones (including the first tier)
    const allMilestones = [];
    for (let i = 0; i < this.rewardsData.tiers.length; i++) {
      allMilestones.push(this.rewardsData.tiers[i]);
    }
    
    // Sort by threshold to ensure earlier tiers come first
    allMilestones.sort((a, b) => a.threshold - b.threshold);
    
    // Helper function to check if a tier has valid content to display
    const hasValidContent = (tier, isReached) => {
      if (!isReached) {
        // Unreached tiers need rewardTitle, otherwise skip
        return tier.rewardTitle && typeof tier.rewardTitle === 'string' && tier.rewardTitle.trim() !== '';
      } else {
        // Reached tiers need postDescription, otherwise skip (no fallback)
        return tier.postDescription && 
               typeof tier.postDescription === 'string' && 
               tier.postDescription.trim() !== '';
      }
    };
    
    // Find cards based on current progress
    // Convert current value to cents for comparison with threshold (which is in cents)
    const currentValueInCents = this.currentValue * 100;
    
    // Filter tiers to only include those with valid content
    const unreachedTiers = allMilestones
      .filter(tier => tier.threshold > currentValueInCents)
      .filter(tier => hasValidContent(tier, false));
    
    const reachedTiers = allMilestones
      .filter(tier => tier.threshold <= currentValueInCents)
      .filter(tier => hasValidContent(tier, true));
    
    // Check if mobile or desktop to determine how many cards to show
    this.isMobile = window.innerWidth < 600;
    
    if (this.isMobile) {
      // Mobile: Show ALL valid cards (for slider)
      // Sort all cards by threshold (low to high)
      const allValidCards = [...unreachedTiers, ...reachedTiers];
      allValidCards.sort((a, b) => a.threshold - b.threshold);
      cardsToShow.push(...allValidCards);
    } else {
      // Desktop: Show exactly 2 cards
    if (unreachedTiers.length >= 2) {
      // If we have 2+ unreached tiers, show the first 2 unreached (earliest)
      cardsToShow.push(...unreachedTiers.slice(0, 2));
    } else if (unreachedTiers.length === 1) {
        // If we have 1 unreached tier, try to add 1 reached tier first
      if (reachedTiers.length > 0) {
        cardsToShow.push(reachedTiers[reachedTiers.length - 1]);
      }
      cardsToShow.push(unreachedTiers[0]);
      } else if (unreachedTiers.length === 0 && reachedTiers.length > 0) {
        // If all tiers are reached (or no valid unreached), show the last 2 reached tiers
      cardsToShow.push(...reachedTiers.slice(-2));
    }
    }
    // If no valid cards at all, cardsToShow will be empty and no cards will be displayed

    // Store current card state for next comparison
    const currentCardState = cardsToShow.map((tier, index) => ({
      threshold: tier.threshold,
      position: index // 0 = left, 1 = right
    }));
    
    // Check if we need to regenerate at all
    const needsRegeneration = previousCards.length === 0 || 
      currentCardState.length !== previousCards.length ||
      currentCardState.some((card, index) => {
        const prev = previousCards[index];
        return !prev || prev.threshold !== card.threshold || prev.position !== card.position;
      });
    
    if (!needsRegeneration) {
      
      // Update only the title text without regenerating cards
      const existingCards = rewardsCardsContainer.querySelectorAll('.reward-card');
      existingCards.forEach((cardDiv, index) => {
        const tier = cardsToShow[index];
        if (!tier) return;
        
        // Convert current value to cents for comparison with threshold
        const currentValueInCents = this.currentValue * 100;
        const remainingAmount = tier.threshold - currentValueInCents;
        const isReached = currentValueInCents >= tier.threshold;
        const titleDiv = cardDiv.querySelector('.reward-title');
        if (titleDiv) {
          // Update title with or without requirement
        if (!isReached && remainingAmount > 0) {
            // Not reached: Modify existing amount or prepend message
            if (tier.rewardTitle && tier.rewardTitle.trim() !== '') {
              const remainingInDollars = (remainingAmount / 100).toFixed(0);
              titleDiv.innerHTML = this.modifySpendMessage(tier.rewardTitle, remainingInDollars);
            }
        } else {
            // Reached: Show postDescription only (no fallback)
            if (tier.postDescription && typeof tier.postDescription === 'string' && tier.postDescription.trim() !== '') {
              titleDiv.innerHTML = tier.postDescription;
            }
          }
        }
      });
      
      return;
    }
    
    // Clear existing cards
    rewardsCardsContainer.innerHTML = '';
    
    // Generate cards (always exactly 2)
    cardsToShow.forEach((tier, index) => {
      // Convert current value to cents for comparison with threshold
      const currentValueInCents = this.currentValue * 100;
      const remainingAmount = tier.threshold - currentValueInCents;
      const isReached = currentValueInCents >= tier.threshold;
      
      const cardDiv = document.createElement('div');
      cardDiv.className = 'reward-card';
      
      // Check if this card is new or changed position
      const previousCard = previousCards.find(c => c.threshold === tier.threshold);
      const isNewCard = !previousCard;
      const hasChangedPosition = previousCard && previousCard.position !== index;
      
      // Add animation only for new cards or position changes
      if (isNewCard || hasChangedPosition) {
        cardDiv.classList.add('animate-in');
        cardDiv.style.animationDelay = `${index * 0.1}s`;
      }
      
      // Add threshold data attribute for connector system
      cardDiv.setAttribute('data-threshold', tier.threshold);
      
      // Add reached class for styling
      if (isReached) {
        cardDiv.classList.add('reached-card');
      }
      
      // Create reward icon dynamically
      const iconDiv = document.createElement('div');
      iconDiv.className = 'reward-icon';
      
      // Determine the image source with fallback logic
      let imageSource = null;
      if (tier.rewardIcon) {
        imageSource = tier.rewardIcon;
      } else if (tier.product && tier.product.featured_image) {
        imageSource = tier.product.featured_image;
      }
      
      // Only create image elements if we have a source
      if (imageSource) {
      // Create dynamic icon container
      const dynamicIcon = document.createElement('div');
      dynamicIcon.className = 'dynamic-icon';
        dynamicIcon.style.setProperty('--background', `url(${imageSource})`);
      
      // Add fallback emoji if image fails to load
      const fallbackEmoji = document.createElement('span');
      fallbackEmoji.textContent = tier.icon;
      fallbackEmoji.style.display = 'none';
      dynamicIcon.appendChild(fallbackEmoji);
      
      // Add error handling for image load
      const img = new Image();
      img.onerror = function() {
        // If image fails to load, show the emoji fallback
          dynamicIcon.style.setProperty('--background', 'none');
        fallbackEmoji.style.display = 'block';
      };
        img.src = imageSource;
      
      iconDiv.appendChild(dynamicIcon);
      }
      
      // Create reward content
      const contentDiv = document.createElement('div');
      contentDiv.className = 'reward-content';
      
      const titleDiv = document.createElement('div');
      titleDiv.className = 'reward-title';
      
      // Show different content based on whether tier is reached
      if (!isReached && remainingAmount > 0) {
        // Not reached: Show rewardTitle with "Spend $X more" requirement
        if (tier.rewardTitle && tier.rewardTitle.trim() !== '') {
          const remainingInDollars = (remainingAmount / 100).toFixed(0);
          
          // Parse the HTML and insert the span within the existing structure
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = tier.rewardTitle;
          
          // Find the paragraph element within the metafield div
          const paragraph = tempDiv.querySelector('p');
          if (paragraph) {
            // Check if the last paragraph's text content ends with period
            const paragraphText = paragraph.textContent.trim();
            const endsWithPeriod = paragraphText.endsWith('.');
            const spendText = endsWithPeriod ? 'Spend' : 'spend';
            
            // Append the spend more text to the paragraph
            paragraph.innerHTML += ` <span style="font-weight: bold;">${spendText} $${remainingInDollars} more.</span>`;
          } else {
            // Fallback: check the entire text content
            const fullText = tempDiv.textContent.trim();
            const endsWithPeriod = fullText.endsWith('.');
            const spendText = endsWithPeriod ? 'Spend' : 'spend';
            
            // Fallback: append to the entire content
            tempDiv.innerHTML += ` <span style="font-weight: bold;">${spendText} $${remainingInDollars} more.</span>`;
          }
          
          titleDiv.innerHTML = tempDiv.innerHTML;
        }
      } else {
        // Reached: Show postDescription only (no fallback)
        if (tier.postDescription && typeof tier.postDescription === 'string' && tier.postDescription.trim() !== '') {
          titleDiv.innerHTML = tier.postDescription;
        }
      }
      
      contentDiv.appendChild(titleDiv);
      
      cardDiv.appendChild(iconDiv);
      cardDiv.appendChild(contentDiv);
      
      rewardsCardsContainer.appendChild(cardDiv);
    });
    
    // Save current card state for next comparison
    this.previousCardState = currentCardState;
    
    // Check if mobile or desktop (use instance variable)
    if (this.isMobile) {
      // Mobile: Initialize Swiper (connector already hidden by CSS)
      this.initMobileSwiper(cardsToShow);
    } else {
      // Desktop: Wait for animations, then create connector (existing logic)
      this.waitForCardAnimations(cardsToShow);
    }
  }

  /**
   * Wait for card animations to complete before generating connectors
   * @param {Array} cardsToShow - Array of cards that were shown
   */
  waitForCardAnimations(cardsToShow) {
    const rewardsCardsContainer = this.querySelector('#rewardsCards');
    if (!rewardsCardsContainer) return;

    // Find cards that have the animate-in class (newly added cards)
    const animatedCards = rewardsCardsContainer.querySelectorAll('.reward-card.animate-in');
    
    // Check if any cards are already fully animated (no animate-in class)
    const allCards = rewardsCardsContainer.querySelectorAll('.reward-card');
    const alreadyAnimatedCards = Array.from(allCards).filter(card => !card.classList.contains('animate-in'));
    
    // Create a unique identifier for this card state
    const cardStateId = `${cardsToShow.length}-${this.currentValue}`;
    
    // If all cards are already animated AND we've already processed this exact state, skip
    if (animatedCards.length === 0 && alreadyAnimatedCards.length > 0 && this.processedCards.has(cardStateId)) {
      return;
    }
    
    // If all cards are already animated but we haven't processed this state yet
    if (animatedCards.length === 0 && alreadyAnimatedCards.length > 0) {
      this.processedCards.add(cardStateId);
      // Still dispatch the event for the first time
    }
    
    if (animatedCards.length === 0) {
      // No animations and no existing cards, dispatch immediately with a small delay to ensure DOM is settled
      setTimeout(() => {
        this.triggerConnectorCreation(cardsToShow);
      }, 50);
      return;
    }

    // Track completed animations
    let completedAnimations = 0;
    const totalAnimations = animatedCards.length;

    const onAnimationEnd = (event) => {
      completedAnimations++;
      
      // Remove the event listener to prevent multiple triggers
      event.target.removeEventListener('animationend', onAnimationEnd);
      
      // Check if all animations are complete
      if (completedAnimations >= totalAnimations) {
        // Mark this state as processed
        this.processedCards.add(cardStateId);
        
        // All animations complete, now trigger connector creation
        setTimeout(() => {
          this.triggerConnectorCreation(cardsToShow);
        }, 10); // Small delay to ensure final layout is applied
      }
    };

    // Add event listeners to all animated cards
    animatedCards.forEach(card => {
      card.addEventListener('animationend', onAnimationEnd);
    });

    // Fallback timeout in case animation events don't fire
    setTimeout(() => {
      // Remove any remaining event listeners
      animatedCards.forEach(card => {
        card.removeEventListener('animationend', onAnimationEnd);
      });
      
      // Mark this state as processed
      this.processedCards.add(cardStateId);
      
      // Dispatch event anyway after maximum animation duration
      this.triggerConnectorCreation(cardsToShow);
    }, 600); // Slightly longer than the 0.5s animation duration
  }

  /**
   * Trigger connector creation directly on the canvas connector instance
   * @param {Array} cardsToShow - Array of cards that were shown
   */
  triggerConnectorCreation(cardsToShow) {
    if (this.canvasConnector) {
      this.canvasConnector.handleAnimationsComplete({
        detail: { 
          cardsCount: cardsToShow.length,
          currentValue: this.currentValue,
          container: this.querySelector('.rewards-container')
        }
      });
    } else {
      console.warn('RewardsProgress: Canvas connector not available');
    }
  }

  /**
   * Modify rewardTitle HTML to update or inject spend message.
   * - If content contains "Spend $<amount>" or "spend $<amount>", only update the amount.
   * - Otherwise, prepend "Spend $<amount> more to " to the start of the first paragraph (or content).
   * @param {string} contentHtml
   * @param {string|number} remainingInDollars
   * @returns {string} Updated HTML
   */
  modifySpendMessage(contentHtml, remainingInDollars) {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = contentHtml;
    const target = tempDiv.querySelector('p') || tempDiv;
    // DOM-aware replacement across nodes: find "Spend" first, then replace first "$<amount>" after it
    const spendRegex = /(Spend|spend)\b/;
    const amountRegex = /\$[ \u00A0]*\d[\d,]*/;

    const walker = document.createTreeWalker(target, NodeFilter.SHOW_TEXT, null);
    let textNodes = [];
    while (walker.nextNode()) textNodes.push(walker.currentNode);

    let spendIndex = -1;
    for (let i = 0; i < textNodes.length; i++) {
      const val = textNodes[i].nodeValue || '';
      if (spendRegex.test(val)) { spendIndex = i; break; }
    }

    let replaced = false;
    if (spendIndex !== -1) {
      // Look for amount in subsequent text nodes (works when amount is inside <strong>)
      for (let i = spendIndex; i < textNodes.length; i++) {
        const node = textNodes[i];
        const val = node.nodeValue || '';
        if (amountRegex.test(val)) {
          node.nodeValue = val.replace(amountRegex, `$${remainingInDollars}`);
          replaced = true;
          break;
        }
      }
      // Edge case: both in same node but not separate
      if (!replaced) {
        const node = textNodes[spendIndex];
        const val = node?.nodeValue || '';
        if (/(Spend|spend)[ \u00A0]*\$[ \u00A0]*\d[\d,]*/.test(val)) {
          node.nodeValue = val.replace(/\$[ \u00A0]*\d[\d,]*/, `$${remainingInDollars}`);
          replaced = true;
        }
      }
    }

    if (!replaced) {
      // Prepend the standardized message
      const existing = (target.innerHTML || '').trim();
      target.innerHTML = `Spend $${remainingInDollars} more to ${existing}`;
    }

    return tempDiv.innerHTML;
  }

  updateProgress() {
    // Check if rewardsData is available before proceeding
    if (!this.rewardsData || !this.rewardsData.tiers) {
      console.warn('updateProgress: rewardsData not available');
      return;
    }

    // Sort tiers by threshold value (lowest to highest) to ensure correct order
    const sortedTiers = [...this.rewardsData.tiers].sort((a, b) => a.threshold - b.threshold);
    const currentValueInCents = this.currentValue * 100;
    const numberOfTiers = sortedTiers.length;
    
    // Milestone visual positions (matching generateMilestones)
    const startPosition = 10;  // First milestone at 10%
    const endPosition = 90;    // Last milestone at 90%
    
    let fillPosition = 0;
    
    // Find which tier range the current value falls into
    if(sortedTiers.length === 0) {
      this.classList.add('disabled')
      return
    } else {
      this.classList.remove('disabled')
    }
    
    const firstTierThreshold = sortedTiers[0].threshold;
    const lastTierThreshold = sortedTiers[numberOfTiers - 1].threshold;
    if (currentValueInCents <= firstTierThreshold) {
      // Before or at first tier - fill from 0 to first milestone position
      const progressToFirstTier = currentValueInCents / firstTierThreshold;
      fillPosition = progressToFirstTier * startPosition;
    } else if (currentValueInCents >= lastTierThreshold) {
      // At or beyond last tier - fill to 100%
      fillPosition = 100;
    } else {
      // Between tiers - calculate based on gap between current and next tier
      let currentTierIndex = -1;
      
      // Find which tier range we're in
      for (let i = 0; i < numberOfTiers - 1; i++) {
        if (currentValueInCents > sortedTiers[i].threshold && currentValueInCents <= sortedTiers[i + 1].threshold) {
          currentTierIndex = i;
          break;
        }
      }
      
      if (currentTierIndex >= 0) {
        const currentTierThreshold = sortedTiers[currentTierIndex].threshold;
        const nextTierThreshold = sortedTiers[currentTierIndex + 1].threshold;
        const gapBetweenTiers = nextTierThreshold - currentTierThreshold;
        const progressInGap = currentValueInCents - currentTierThreshold;
        const percentageInGap = progressInGap / gapBetweenTiers;
        
        // Calculate milestone positions
        const spacing = numberOfTiers === 1 ? 0 : (endPosition - startPosition) / (numberOfTiers - 1);
        const currentMilestonePosition = startPosition + (spacing * currentTierIndex);
        const nextMilestonePosition = startPosition + (spacing * (currentTierIndex + 1));
        const visualGap = nextMilestonePosition - currentMilestonePosition;
        
        // Fill position is current milestone + percentage of visual gap
        fillPosition = currentMilestonePosition + (percentageInGap * visualGap);
      }
    }

    const progressFill = this.querySelector('.progress-fill');
    if (progressFill) {
      progressFill.style.width = `${fillPosition}%`;
    }

    const currentValueDisplay = this.querySelector('#currentValueDisplay');
    if (currentValueDisplay) {
      currentValueDisplay.textContent = this.currentValue;
    }

    const milestoneElements = this.querySelectorAll('.milestone');
    milestoneElements.forEach((element, index) => {
      const tierThreshold = sortedTiers[index].threshold;
      const tierThresholdInDollars = tierThreshold / 100;
      element.classList.remove('active', 'reached', 'locked');

      if (this.currentValue > tierThresholdInDollars) {
        element.classList.add('reached');
      } else if (this.currentValue === tierThresholdInDollars) {
        element.classList.add('reached');
      } else {
        element.classList.add('locked');
      }
    });

    // Update reward cards
    this.generateRewardCards();
  }

  // Public API methods
  setValue(value) {
    this.currentValue = Math.max(this.minValue, Math.min(this.maxValue, value));
    this.setAttribute('data-current-value', this.currentValue);
    this.updateProgress();
    this.dispatchEvent(new CustomEvent('valueChanged', { 
      detail: { currentValue: this.currentValue } 
    }));
  }

  getValue() {
    return this.currentValue;
  }

  // Attribute change handling
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'data-current-value' && newValue !== null) {
      this.currentValue = parseInt(newValue) || 0;
      this.updateProgress();
    } else if (name === 'data-step' && newValue !== null) {
      this.step = parseInt(newValue) || 10;
    }
  }

  /**
   * Check for reached tiers that have products
   * Called on component mount
   */
  checkReachedTiersWithProducts() {
    if (!this.rewardsData || !this.rewardsData.tiers || !this.rewardsData.cart) {
      return;
    }

    const currentValueInCents = this.currentValue * 100;
    const cart = this.rewardsData.cart;

    // Collect all reached tier product IDs
    const reachedTierProductIds = new Set();

    // Find all reached tiers that have products
    this.rewardsData.tiers.forEach(tier => {
      const isReached = currentValueInCents >= tier.threshold;
      const hasProduct = tier.product && tier.product.variants && tier.product.variants.length > 0;

      if (isReached && hasProduct) {
        reachedTierProductIds.add(tier.product.id);

        // Check if this product is already in cart
        const isInCart = this.isProductInCart(tier.product.id, cart);

        if (isInCart) {
          // Product already in cart according to rewardsData payload - do nothing
          return;
        }

        // Product not in data payload cart - double check live cart before adding
        this.isProductInCartLive(tier.product.id)
          .then((exists) => {
            if (exists) {
              return;
            }

            // Product not in cart - add it
            const availableVariant = tier.product.variants.find(v => v.available);
            
            if (availableVariant) {
              // this.addToCart(availableVariant.id, 1, { isGiftItem: true });
              this.addToCart(availableVariant.id, 1, { isGiftItem: true })
              .then(() => {
                // If user is on cart page, reload after adding gift
                if (window.location.pathname.includes('/cart') || window.location.href.includes('/cart')) {
                  window.location.reload();
                }
              })
              .catch((error) => {
                console.warn(`[RewardsProgress] Failed to add gift product ${tier.product.id}`, error);
              });
            }
          })
          .catch((error) => {
            console.warn(`[RewardsProgress] Failed live cart check for product ${tier.product.id}`, error);
          });
      }
    });

    // Check for invalid gift items (gift items that don't belong to reached tiers)
    const invalidGiftItems = this.findInvalidGiftItems(cart, reachedTierProductIds);

    if (invalidGiftItems.length > 0) {
      // Build updates object to remove all invalid items
      const updates = {};
      invalidGiftItems.forEach(item => {
        updates[item.variant_id] = 0; // Set quantity to 0 to remove
      });
      
      // Remove invalid gift items from cart
      this.updateCart(updates);
    }
  }

  /**
   * Check if a product is in the cart
   * @param {number} productId - The product ID to check
   * @param {object} cart - The cart object
   * @returns {boolean} True if product is in cart
   */
  isProductInCart(productId, cart) {
    if (!cart || !cart.items || !Array.isArray(cart.items)) {
      return false;
    }

    return cart.items.some(item => item.product_id === productId);
  }

  async isProductInCartLive(productId) {
    try {
      const cart = await this.fetchCart();
      if (!cart || !Array.isArray(cart.items)) return false;
      console.log('isProductInCartLive', cart.items);
      return cart.items.some(item => item && Number(item.product_id) === Number(productId));
    } catch (error) {
      console.warn(`[RewardsProgress] Failed to fetch cart for product check ${productId}`, error);
      return false;
    }
  }

  /**
   * Find gift items in cart that don't belong to reached tiers
   * @param {object} cart - The cart object
   * @param {Set} reachedTierProductIds - Set of product IDs from reached tiers
   * @returns {Array} Array of invalid gift items with their details
   */
  findInvalidGiftItems(cart, reachedTierProductIds) {
    if (!cart || !cart.items || !Array.isArray(cart.items)) {
      return [];
    }

    const invalidItems = [];

    cart.items.forEach(item => {
      // Check if item is a gift item
      const isGiftItem = item.properties && item.properties.isGiftItem === true;

      if (isGiftItem) {
        // Check if this gift item's product is NOT in the reached tiers
        const isValidGift = reachedTierProductIds.has(item.product_id);

        if (!isValidGift) {
          invalidItems.push({
            variant_id: item.variant_id,
            product_id: item.product_id,
            title: item.product_title,
            key: item.key
          });
        }
      }
    });

    return invalidItems;
  }

  // ===== CART OPERATION METHODS =====

  /**
   * Add item to cart
   * @param {number} variantId - The variant ID to add
   * @param {number} quantity - Quantity to add (default: 1)
   * @param {object} properties - Line item properties (optional)
   * @returns {Promise<object>} Cart response data
   */
  async addToCart(variantId, quantity = 1, properties = {}) {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: quantity,
            properties: properties
          }],
          sections: ['gift-item-data', 'mini-cart', 'cart-icon-bubble', 'mobile-cart-icon-bubble'],
          sections_url: window.location.pathname
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        // Update mini-cart
        const miniCart = document.querySelector('mini-cart');
        if (miniCart) {
          miniCart.renderContents(data);
        }

        // Dispatch event
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
          detail: { product: data }
        }));

        // Enforce single-quantity rule for gift items
        await this.normalizeGiftItems();

        return data;
      } else {
        throw new Error(`Failed to add to cart: ${await response.text()}`);
      }
    } catch (error) {
      console.warn('Error adding to cart:', error);
      throw error;
    }
  }

  /**
   * Update cart items
   * @param {object} updates - Object with variant IDs as keys and quantities as values
   *                           Example: { 12345: 2, 67890: 0 }
   * @returns {Promise<object>} Cart response data
   */
  async updateCart(updates) {
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          updates,
          sections: ['gift-item-data', 'mini-cart', 'cart-icon-bubble', 'mobile-cart-icon-bubble'],
          sections_url: window.location.pathname
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        // Update mini-cart
        const miniCart = document.querySelector('mini-cart');
        if (miniCart) {
          miniCart.renderContents(data);
        }

        // Dispatch event
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
          detail: { product: data }
        }));

        // Enforce single-quantity rule for gift items
        await this.normalizeGiftItems();

        return data;
      } else {
        throw new Error(`Failed to update cart: ${await response.text()}`);
      }
    } catch (error) {
      console.warn('Error updating cart:', error);
      throw error;
    }
  }

  /**
   * Remove item from cart (convenience method)
   * @param {number} variantId - The variant ID to remove
   * @returns {Promise<object>} Cart response data
   */
  async removeFromCart(variantId) {
    return this.updateCart({ [variantId]: 0 });
  }

  /**
   * Change cart item quantity
   * @param {number} variantId - The variant ID
   * @param {number} quantity - New quantity
   * @returns {Promise<object>} Cart response data
   */
  async changeQuantity(variantId, quantity) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity,
          sections: ['gift-item-data', 'mini-cart', 'cart-icon-bubble', 'mobile-cart-icon-bubble'],
          sections_url: window.location.pathname
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        // Update mini-cart
        const miniCart = document.querySelector('mini-cart');
        if (miniCart) {
          miniCart.renderContents(data);
        }

        // Dispatch event
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
          detail: { product: data }
        }));

        // Enforce single-quantity rule for gift items
        await this.normalizeGiftItems();

        return data;
      } else {
        throw new Error(`Failed to change quantity: ${await response.text()}`);
      }
    } catch (error) {
      console.warn('Error changing quantity:', error);
      throw error;
    }
  }

  // ===== MOBILE SWIPER METHODS =====

  /**
   * Fetch current cart
   */
  async fetchCart() {
    try {
      const res = await fetch('/cart.js')
      return await res.json()
    } catch (_) {
      return null
    }
  }

  /**
   * Change quantity by line item key (precise)
   */
  async changeLineQuantityByKey(lineKey, quantity) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: String(lineKey), quantity, sections: ['gift-item-data', 'mini-cart', 'cart-icon-bubble', 'mobile-cart-icon-bubble'], sections_url: window.location.pathname })
      })
      if (!response.ok) return null
      const data = await response.json()
      const miniCart = document.querySelector('mini-cart');
      if (miniCart) miniCart.renderContents(data)
      document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: data } }))
      return data
    } catch (_) {
      return null
    }
  }

  /**
   * Ensure gift items (isGiftItem: true) never exceed quantity 1 total.
   * If duplicates exist, keep the first, remove others. If a single gift has qty>1, set to 1.
   */
  async normalizeGiftItems() {
    const cart = await this.fetchCart()
    if (!cart || !Array.isArray(cart.items)) return

    const seen = new Set()
    for (const item of cart.items) {
      const isGift = item.properties && item.properties.isGiftItem === true
      if (!isGift) continue

      const productKey = `${item.product_id}`
      if (!seen.has(productKey)) {
        seen.add(productKey)
        if (item.quantity !== 1) {
          await this.changeLineQuantityByKey(item.key, 1)
        }
      } else {
        // Any subsequent gift line of same product should be removed
        await this.changeLineQuantityByKey(item.key, 0)
      }
    }
  }

  /**
   * Initialize Swiper slider for mobile
   * @param {Array} cardsToShow - Array of cards currently displayed
   */
  initMobileSwiper(cardsToShow) {
    const rewardsCardsContainer = this.querySelector('.rewards-cards');
    if (!rewardsCardsContainer) return;
    
    // Destroy existing Swiper if present to avoid conflicts
    this.destroyMobileSwiper();
    
    // Add swiper-slide class to each card BEFORE wrapping
    const cards = rewardsCardsContainer.querySelectorAll('.reward-card');
    cards.forEach(card => {
      if (!card.classList.contains('swiper-slide')) {
        card.classList.add('swiper-slide');
      }
    });
    
    // Create Swiper structure
    const swiperComponent = document.createElement('slider-component-swiper');
    swiperComponent.setAttribute('data-slides-per-mobile', '1.3');
    swiperComponent.classList.add('rewards-swiper');
    
    const sliderWrapper = document.createElement('div');
    sliderWrapper.className = 'slider-wrapper swiper';
    
    // Move cards container into Swiper
    const parent = rewardsCardsContainer.parentNode;
    parent.insertBefore(swiperComponent, rewardsCardsContainer);
    swiperComponent.appendChild(sliderWrapper);
    sliderWrapper.appendChild(rewardsCardsContainer);
    
    // Add swiper-wrapper class to cards container
    rewardsCardsContainer.classList.add('swiper-wrapper');
    
    // Manually initialize Swiper since slider-component-swiper might not work as expected
    // Use Swiper directly if available
    if (typeof Swiper !== 'undefined') {
      setTimeout(() => {
        const swiper = new Swiper(sliderWrapper, {
          slidesPerView: 'auto',
          spaceBetween: 10,
          centeredSlides: true,  // Enable this for center snapping
          freeMode: false,
          grabCursor: true,
          touchRatio: 1,
          slideToClickedSlide: true,  // Enable click to snap
          allowTouchMove: true,
          resistance: true,
          resistanceRatio: 0.85,
          speed: 400,  // Smooth snap animation
          // These help with centering:
          centeredSlidesBounds: true,  // Keeps centered slides within bounds
          normalizeSlideIndex: true,
          watchSlidesProgress: true
        });
        
        // Store swiper instance
        swiperComponent.slider = swiper;
        
        // Position to relevant slide
        this.positionMobileSwiper(cardsToShow);
      }, 100);
    } else {
      // Fallback: Try using the theme's slider-component-swiper
      setTimeout(() => {
        this.positionMobileSwiper(cardsToShow);
      }, 300);
    }
  }

  /**
   * Position the mobile Swiper to show the most relevant card
   * @param {Array} cardsToShow - Array of cards currently displayed
   */
  positionMobileSwiper(cardsToShow) {
    if (window.innerWidth >= 600) return;
    
    const swiperComponent = this.querySelector('slider-component-swiper');
    if (!swiperComponent || !swiperComponent.slider) return;
    
    const currentValueInCents = this.currentValue * 100;
    
    // Determine which card to show
    // Priority: First unreached tier, otherwise last reached tier
    let targetIndex = 0;
    let foundFirstUnreached = false;
    
    cardsToShow.forEach((tier, index) => {
      const isReached = currentValueInCents >= tier.threshold;
      
      if (!isReached && !foundFirstUnreached) {
        // First unreached tier - show this
        targetIndex = index;
        foundFirstUnreached = true;
      } else if (isReached && !foundFirstUnreached) {
        // Update to latest reached tier (only if no unreached found yet)
        targetIndex = index;
      }
    });
    
    // Slide to the target card (0 = instant, no animation)
    swiperComponent.slider.slideTo(targetIndex, 0);
  }

  /**
   * Destroy mobile Swiper and restore original structure
   */
  destroyMobileSwiper() {
    const swiperComponent = this.querySelector('slider-component-swiper');
    if (!swiperComponent) return;
    
    const rewardsCards = swiperComponent.querySelector('.rewards-cards');
    
    // Destroy Swiper instance if it exists
    if (swiperComponent.slider) {
      swiperComponent.slider.destroy(true, true);
    }
    
    // Restore original structure
    if (rewardsCards) {
      // Remove Swiper classes
      rewardsCards.classList.remove('swiper-wrapper');
      rewardsCards.querySelectorAll('.reward-card').forEach(card => {
        card.classList.remove('swiper-slide');
      });
      
      // Move cards back to original parent
      const parent = swiperComponent.parentNode;
      if (parent) {
        parent.insertBefore(rewardsCards, swiperComponent);
      }
    }
    
    // Remove Swiper component
    swiperComponent.remove();
  }

  /**
   * Handle window resize for mobile/desktop switching
   */
  handleResize() {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      const currentBreakpoint = window.innerWidth < 600 ? 'mobile' : 'desktop';
      
      // Only regenerate if crossing breakpoint
      if (this.lastBreakpoint && this.lastBreakpoint !== currentBreakpoint) {
        // Crossing breakpoint - fully regenerate
        if (currentBreakpoint === 'desktop') {
          // Switching to desktop: destroy Swiper, restore grid
          this.destroyMobileSwiper();
        }
        
        // Regenerate cards (will auto-detect mobile/desktop)
        this.generateRewardCards();
      }
      
      this.lastBreakpoint = currentBreakpoint;
    }, 150);
  }
}

// Register the custom element
customElements.define('rewards-progress', RewardsProgress);

/**
 * Canvas Connector System for Rewards Progress Component
 * 
 * This module provides canvas-based connector functionality that draws
 * visual lines connecting milestone centers to their corresponding reward cards.
 */

class CanvasConnector {
  constructor() {
    this.canvas = null;
    this.ctx = null;
    this.connectors = [];
    this.previousConnectors = [];
    this.isAnimating = false;
    this.animationFrame = null;
    this.animationProgress = 0;
    this.animationsReady = false; // Flag to prevent connector creation before animations complete
    
    // Configuration
    this.config = {
      lineWidth: 1,
      lineColor: '#b0b0b0',
      activeLineColor: '#e8b4a8',
      reachedLineColor: '#4a6fa5',
      lineOpacity: 0.7,
      animationDuration: 600, // Animation duration in ms
      curveIntensity: 0.3
    };
    
    // Bind methods
    this.updateConnectors = this.updateConnectors.bind(this);
    this.handleResize = this.debounce(this.handleResize.bind(this), 250);
  }

  /**
   * Initialize the canvas connector system
   * @param {HTMLElement} container - The rewards container element
   */
  init(container) {
    this.container = container;
    this.canvas = container.querySelector('#connectorCanvas');
    
    if (!this.canvas) {
      console.warn('CanvasConnector: Canvas element not found');
      return false;
    }
    
    this.ctx = this.canvas.getContext('2d');
    this.animationsReady = false; // Reset flag on initialization
    this.setupCanvas();
    this.setupEventListeners();
    
    return true;
  }

  /**
   * Set up canvas dimensions and basic properties
   */
  setupCanvas() {
    const rect = this.container.getBoundingClientRect();
    const devicePixelRatio = window.devicePixelRatio || 1;
    
    // Set canvas size to match container (accounting for device pixel ratio)
    this.canvas.width = rect.width * devicePixelRatio;
    this.canvas.height = rect.height * devicePixelRatio;
    
    // Set canvas display size
    this.canvas.style.width = rect.width + 'px';
    this.canvas.style.height = rect.height + 'px';
    
    // Scale the context to match device pixel ratio
    this.ctx.scale(devicePixelRatio, devicePixelRatio);
    
    // Set canvas context properties
    this.ctx.lineCap = 'round';
    this.ctx.lineJoin = 'round';
    this.ctx.globalAlpha = this.config.lineOpacity;
  }

  /**
   * Set up event listeners for resize and updates
   */
  setupEventListeners() {
    window.addEventListener('resize', this.handleResize);
    
    // Note: Event listener will be attached by the RewardsProgress component
    // to avoid conflicts with multiple instances
    
    // Note: MutationObserver removed - connector updates are now handled explicitly
    // through the waitForCardAnimations() system for better control
    
    // Watch for image load events that might change card dimensions
    this.setupImageLoadWatchers();
    
    // Set up ResizeObserver for card dimension changes
    this.setupResizeObserver();
  }
  
  /**
   * Set up ResizeObserver to watch for card dimension changes
   */
  setupResizeObserver() {
    if (!window.ResizeObserver) return;
    
    const cardsContainer = this.container.querySelector('#rewardsCards');
    if (!cardsContainer) return;
    
    this.resizeObserver = new ResizeObserver((entries) => {
      // Only react to resize after animations are complete
      if (!this.animationsReady) {
        return;
      }
      
      // Debounce the connector updates to avoid excessive redraws
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        this.updateConnectors();
      }, 100);
    });
    
    // Observe all existing cards
    const cards = cardsContainer.querySelectorAll('.reward-card');
    cards.forEach(card => this.resizeObserver.observe(card));
    
    // Store reference for future cards
    this.cardsContainer = cardsContainer;
  }
  
  /**
   * Update ResizeObserver to watch new cards
   */
  updateResizeObserver() {
    if (!this.resizeObserver || !this.cardsContainer) return;
    
    // Get all current cards
    const currentCards = this.cardsContainer.querySelectorAll('.reward-card');
    
    // Check if we have new cards to observe
    currentCards.forEach(card => {
      // Check if this card is already being observed
      const isObserved = this.resizeObserver._observedElements?.has(card);
      if (!isObserved) {
        this.resizeObserver.observe(card);
      }
    });
  }
  
  /**
   * Set up watchers for image load events
   */
  setupImageLoadWatchers() {
    const cardsContainer = this.container.querySelector('#rewardsCards');
    if (!cardsContainer) return;
    
    // Watch for image loads in reward cards
    const imageObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // Check if it's a reward card or contains images
            const cards = node.classList?.contains('reward-card') ? [node] : node.querySelectorAll?.('.reward-card');
            if (cards) {
              cards.forEach(card => {
                const images = card.querySelectorAll('img, .dynamic-icon');
                images.forEach(img => {
                  if (img.tagName === 'IMG') {
                    img.addEventListener('load', () => {
                      if (this.animationsReady) {
                        this.updateConnectors();
                      }
                    });
                    img.addEventListener('error', () => {
                      if (this.animationsReady) {
                        this.updateConnectors();
                      }
                    });
                  }
                });
              });
            }
          }
        });
      });
    });
    
    imageObserver.observe(cardsContainer, {
        childList: true,
        subtree: true
      });
      
    this.imageObserver = imageObserver;
  }

  /**
   * Handle window resize
   */
  handleResize() {
    this.setupCanvas();
    this.updateConnectors();
  }

  /**
   * Handle animations complete event from rewards component
   * This is the single entry point for connector creation
   */
  handleAnimationsComplete(event) {
    // Canvas should already be initialized, just update connectors
    if (!this.ctx) {
      console.warn('CanvasConnector: Canvas not initialized, skipping connector creation');
      return;
    }
    
    // Set the flag to allow connector creation
    this.animationsReady = true;
    
    // Now generate connectors with perfect positioning
    this.updateConnectors();
  }

  /**
   * Calculate positions of milestones and cards
   * @returns {Array} Array of connector objects with start/end positions
   */
  calculateConnectorPositions() {
    const connectors = [];
    
    // Get all milestone elements
    const milestones = this.container.querySelectorAll('.milestone');
    const cards = this.container.querySelectorAll('.reward-card');
    
    if (milestones.length === 0 || cards.length === 0) {
      return connectors;
    }
    
    // Calculate milestone positions
    const milestonePositions = Array.from(milestones).map(milestone => {
      const rect = milestone.getBoundingClientRect();
      const containerRect = this.container.getBoundingClientRect();
      
      return {
        element: milestone,
        x: rect.left + rect.width / 2 - containerRect.left,
        y: rect.bottom - containerRect.top, // Bottom of milestone (below the icon)
        threshold: parseInt(milestone.dataset.threshold) || 0
      };
    });
    
    // Calculate card positions
    const cardPositions = Array.from(cards).map(card => {
      const rect = card.getBoundingClientRect();
      const containerRect = this.container.getBoundingClientRect();
      
      return {
        element: card,
        x: rect.left + rect.width / 2 - containerRect.left,
        y: rect.top - containerRect.top, // Top of card
        threshold: parseInt(card.dataset.threshold) || 0
      };
    });
    
    // Match cards to milestones - only connect visible cards
    cardPositions.forEach(card => {
      // Find the milestone with matching threshold
      const milestone = milestonePositions.find(m => m.threshold === card.threshold);
      
      if (milestone) {
        connectors.push({
          start: { x: milestone.x, y: milestone.y },
          end: { x: card.x, y: card.y },
          milestone: milestone.element,
          card: card.element,
          threshold: milestone.threshold,
          isReached: milestone.element.classList.contains('reached'),
          isActive: milestone.element.classList.contains('active')
        });
      }
    });
    
    return connectors;
  }

  /**
   * Extract threshold value from card element
   * @param {HTMLElement} card - The card element
   * @returns {number} The threshold value
   */
  getCardThreshold(card) {
    // Try to get threshold from data attribute
    if (card.dataset.threshold) {
      return parseInt(card.dataset.threshold);
    }
    
    // Try to extract from reward requirement text
    const requirement = card.querySelector('.reward-requirement');
    if (requirement) {
      const match = requirement.textContent.match(/\$(\d+)/);
      if (match) {
        return parseInt(match[1]);
      }
    }
    
    return 0;
  }

  /**
   * Draw a single connector line
   * @param {Object} connector - Connector object with start/end positions
   * @param {number} progress - Animation progress (0 to 1)
   */
  drawConnector(connector, progress = 1) {
    const { start, end, isReached, isActive } = connector;
    
    // Set consistent line style (no color changes based on state)
    this.ctx.strokeStyle = this.config.lineColor;
    this.ctx.lineWidth = this.config.lineWidth;
    
    // Calculate total vertical distance from milestone to card
    const totalVerticalDistance = end.y - start.y;
    
    // Dynamic vertical segments based on actual distance
    const topVerticalLength = totalVerticalDistance / 2; // Half of total distance
    const bottomVerticalLength = (totalVerticalDistance / 2) - 1; // Half minus 1px for visual gap
    
    const topVerticalEnd = start.y + topVerticalLength;
    const bottomVerticalStart = end.y - bottomVerticalLength;
    
    // Calculate total path length for animation
    const segment1Length = topVerticalLength; // First vertical
    const segment2Length = Math.abs(end.x - start.x); // Horizontal
    const segment3Length = bottomVerticalLength; // Second vertical
    const totalLength = segment1Length + segment2Length + segment3Length;
    
    // Current drawing length based on progress
    const currentLength = totalLength * progress;
    
    this.ctx.beginPath();
    this.ctx.moveTo(start.x, start.y);
    
    if (currentLength <= segment1Length) {
      // Drawing first vertical segment
      const y = start.y + currentLength;
      this.ctx.lineTo(start.x, y);
    } else if (currentLength <= segment1Length + segment2Length) {
      // Drawing horizontal segment
      this.ctx.lineTo(start.x, topVerticalEnd);
      const horizontalProgress = (currentLength - segment1Length) / segment2Length;
      const x = start.x + (end.x - start.x) * horizontalProgress;
      this.ctx.lineTo(x, topVerticalEnd);
    } else {
      // Drawing second vertical segment
      this.ctx.lineTo(start.x, topVerticalEnd);
      this.ctx.lineTo(end.x, topVerticalEnd);
      const verticalProgress = (currentLength - segment1Length - segment2Length) / segment3Length;
      const y = bottomVerticalStart + (end.y - bottomVerticalStart) * verticalProgress;
      this.ctx.lineTo(end.x, y);
    }
    
    this.ctx.stroke();
  }
  
  /**
   * Draw a connector with fade out animation (for disconnecting)
   * @param {Object} connector - Connector object with start/end positions
   * @param {number} fadeProgress - Fade progress (0 = visible, 1 = invisible)
   */
  drawConnectorFadeOut(connector, fadeProgress) {
    const { start, end } = connector;
    
    // Set opacity based on fade progress
    const opacity = 1 - fadeProgress;
    this.ctx.globalAlpha = opacity * this.config.lineOpacity;
    
    this.ctx.strokeStyle = this.config.lineColor;
    this.ctx.lineWidth = this.config.lineWidth;
    
    // Use same dynamic vertical segments as main draw method
    const totalVerticalDistance = end.y - start.y;
    const topVerticalLength = totalVerticalDistance / 2; // Half of total distance
    const bottomVerticalLength = (totalVerticalDistance / 2) - 1; // Half minus 1px for visual gap
    
    const topVerticalEnd = start.y + topVerticalLength;
    const bottomVerticalStart = end.y - bottomVerticalLength;
    
    this.ctx.beginPath();
    this.ctx.moveTo(start.x, start.y);
    this.ctx.lineTo(start.x, topVerticalEnd);
    this.ctx.lineTo(end.x, topVerticalEnd);
    this.ctx.lineTo(end.x, bottomVerticalStart);
    this.ctx.lineTo(end.x, end.y);
    this.ctx.stroke();
    
    // Reset global alpha
    this.ctx.globalAlpha = this.config.lineOpacity;
  }

  /**
   * Draw a connection dot at the specified position
   * @param {number} x - X coordinate
   * @param {number} y - Y coordinate
   * @param {boolean} isReached - Whether the connection is reached
   * @param {boolean} isActive - Whether the connection is active
   * @param {number} radius - Optional custom radius (default based on state)
   */
  drawConnectionDot(x, y, isReached, isActive, radius = null) {
    if (radius === null) {
      radius = isReached ? 4 : 3;
    }
    
    this.ctx.beginPath();
    this.ctx.arc(x, y, radius, 0, 2 * Math.PI);
    
    if (isReached) {
      this.ctx.fillStyle = this.config.reachedLineColor;
    } else if (isActive) {
      this.ctx.fillStyle = this.config.activeLineColor;
    } else {
      this.ctx.fillStyle = this.config.lineColor;
    }
    
    this.ctx.fill();
    
    // Add white border
    this.ctx.strokeStyle = '#ffffff';
    this.ctx.lineWidth = 1;
    this.ctx.stroke();
  }

  /**
   * Clear the canvas
   */
  clearCanvas() {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }

  /**
   * Update and redraw all connectors with animation
   */
  updateConnectors() {
    if (!this.ctx) return;
    
    // Block connector creation until animations are explicitly complete
    if (!this.animationsReady) {
      return;
    }
    
    // Cancel any ongoing animation
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }
    
    // Update ResizeObserver to watch new cards
    this.updateResizeObserver();
    
    // Store previous connectors for fade-out animation
    this.previousConnectors = [...this.connectors];
    
    // Calculate new connectors
    const newConnectors = this.calculateConnectorPositions();
    
    // Identify which connectors are new (connecting) and which are removed (disconnecting)
    const newConnectorKeys = newConnectors.map(c => `${c.threshold}`);
    const oldConnectorKeys = this.previousConnectors.map(c => `${c.threshold}`);
    
    const connectingConnectors = newConnectors.filter(
      c => !oldConnectorKeys.includes(`${c.threshold}`)
    );
    const disconnectingConnectors = this.previousConnectors.filter(
      c => !newConnectorKeys.includes(`${c.threshold}`)
    );
    
    // For persisting connectors, check if position changed
    const persistingConnectors = [];
    const reconnectingConnectors = []; // Connectors that need to disconnect and reconnect at new position
    
    newConnectors.forEach(newConn => {
      if (oldConnectorKeys.includes(`${newConn.threshold}`)) {
        const oldConn = this.previousConnectors.find(c => c.threshold === newConn.threshold);
        
        // Check if position changed
        const positionChanged = 
          Math.abs(oldConn.start.x - newConn.start.x) > 1 ||
          Math.abs(oldConn.start.y - newConn.start.y) > 1 ||
          Math.abs(oldConn.end.x - newConn.end.x) > 1 ||
          Math.abs(oldConn.end.y - newConn.end.y) > 1;
        
        if (positionChanged) {
          // Treat position change as disconnect (old) + reconnect (new)
          disconnectingConnectors.push(oldConn);
          reconnectingConnectors.push(newConn);
        } else {
          persistingConnectors.push(newConn);
        }
      }
    });
    
    // Merge reconnecting connectors with connecting connectors for the connect phase
    const allConnectingConnectors = [...connectingConnectors, ...reconnectingConnectors];
    
    // Update connectors list
    this.connectors = newConnectors;
    
    // Start animation
    if (allConnectingConnectors.length > 0 || disconnectingConnectors.length > 0) {
      this.animateConnectorChanges(persistingConnectors, allConnectingConnectors, disconnectingConnectors);
    } else {
      // No animation needed, just draw
      this.clearCanvas();
      this.connectors.forEach(connector => {
        this.drawConnector(connector, 1);
      });
    }
  }
  
  /**
   * Animate connector changes (connect and disconnect)
   * Sequential animation: disconnect first, then connect
   */
  animateConnectorChanges(persisting, connecting, disconnecting) {
    const startTime = performance.now();
    const halfDuration = this.config.animationDuration / 2;
    
    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const totalProgress = Math.min(elapsed / this.config.animationDuration, 1);
      
      // Clear canvas
      this.clearCanvas();
      
      // Always draw static persisting connectors
      persisting.forEach(connector => {
        this.drawConnector(connector, 1);
      });
      
      if (disconnecting.length > 0 && connecting.length > 0) {
        // Sequential animation: disconnect then connect
        if (elapsed < halfDuration) {
          // Phase 1: Fade out disconnecting connectors (first half)
          const disconnectProgress = Math.min(elapsed / halfDuration, 1);
          const easeDisconnect = this.easeInOutCubic(disconnectProgress);
          
          disconnecting.forEach(connector => {
            this.drawConnectorFadeOut(connector, easeDisconnect);
          });
        } else {
          // Phase 2: Draw in connecting connectors (second half)
          const connectProgress = Math.min((elapsed - halfDuration) / halfDuration, 1);
          const easeConnect = this.easeInOutCubic(connectProgress);
          
          connecting.forEach(connector => {
            this.drawConnector(connector, easeConnect);
          });
        }
      } else if (disconnecting.length > 0) {
        // Only disconnecting
        const disconnectProgress = Math.min(elapsed / this.config.animationDuration, 1);
        const easeDisconnect = this.easeInOutCubic(disconnectProgress);
        
        disconnecting.forEach(connector => {
          this.drawConnectorFadeOut(connector, easeDisconnect);
        });
      } else if (connecting.length > 0) {
        // Only connecting
        const connectProgress = Math.min(elapsed / this.config.animationDuration, 1);
        const easeConnect = this.easeInOutCubic(connectProgress);
        
        connecting.forEach(connector => {
          this.drawConnector(connector, easeConnect);
        });
      }
      
      if (totalProgress < 1) {
        this.animationFrame = requestAnimationFrame(animate);
      } else {
        this.isAnimating = false;
      }
    };
    
    this.isAnimating = true;
    this.animationFrame = requestAnimationFrame(animate);
  }
  
  /**
   * Easing function for smooth animation
   */
  easeInOutCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }


  /**
   * Update connector configuration
   * @param {Object} newConfig - New configuration options
   */
  updateConfig(newConfig) {
    this.config = { ...this.config, ...newConfig };
  }

  /**
   * Clean up resources and event listeners
   */
  destroy() {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }
    
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    
    // Clean up observers
    if (this.imageObserver) {
      this.imageObserver.disconnect();
    }
    
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    
    window.removeEventListener('resize', this.handleResize);
  }

  /**
   * Debounce function for performance optimization
   * @param {Function} func - Function to debounce
   * @param {number} wait - Wait time in milliseconds
   * @returns {Function} Debounced function
   */
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

// Export for use in other modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CanvasConnector;
} else if (typeof window !== 'undefined') {
  window.CanvasConnector = CanvasConnector;
}

// Auto-initialize when rewards component is ready (fallback for backwards compatibility)
// Note: The canvas connector now initializes automatically when the component mounts,
// so this is primarily a fallback for edge cases where the component was already
// mounted before this script loaded.
document.addEventListener('DOMContentLoaded', function() {
  // Wait for the component and respect its configured animation delay
  const rewardsComponent = document.querySelector('rewards-progress');
  if (!rewardsComponent) return;

  const delayAttr = parseInt(rewardsComponent.getAttribute('data-animation-delay'));
  const delay = Number.isFinite(delayAttr) ? delayAttr : 500;

  setTimeout(() => {
    if (!rewardsComponent) return;
    // If the component has already initialized the connector after animations, skip
    if (rewardsComponent.canvasConnector) return;
    if (typeof rewardsComponent.initializeCanvasConnector === 'function') {
      rewardsComponent.initializeCanvasConnector();
    }
  }, delay);
});
